import { App as AntApp, ConfigProvider } from 'antd';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PreviewBanner } from './components/PreviewBanner';
import { RequireAuth } from './components/RequireAuth';
import { AuthProvider, useAuth } from './lib/auth';
import { MeProvider } from './lib/me';
import { theme } from './lib/theme';
import { AcceptInviteScene } from './scenes/AcceptInviteScene';
import { AdminUsersScene } from './scenes/AdminUsersScene';
import { DownloadsScene } from './scenes/DownloadsScene';
import { EventScene } from './scenes/EventScene';
import { EventsScene } from './scenes/EventsScene';
import { LoginScene } from './scenes/LoginScene';
import { PreviewLandingScene } from './scenes/PreviewLandingScene';
import { RehearsalScene } from './scenes/RehearsalScene';
import { SlideLicensesScene } from './scenes/SlideLicensesScene';
import { SlideSimulatorScene } from './scenes/SlideSimulatorScene';
import { SpeakerLandingScene } from './scenes/SpeakerLandingScene';
import { SpeakersScene } from './scenes/SpeakersScene';
import { SrtScene } from './scenes/SrtScene';
import { StageScene } from './scenes/StageScene';
import { StreamsScene } from './scenes/StreamsScene';
import { VerifyMagicLinkScene } from './scenes/VerifyMagicLinkScene';
import { WaitingRoomScene } from './scenes/WaitingRoomScene';

function HomeRedirect() {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (!session) return <Navigate to="/login" replace />;
  return <Navigate to={session.role === 'team' ? '/events' : '/me'} replace />;
}

export function App() {
  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <AuthProvider>
        <MeProvider>
          <BrowserRouter>
            <PreviewBanner />
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/preview" element={<PreviewLandingScene />} />
              <Route path="/login" element={<LoginScene />} />
              <Route path="/auth/verify" element={<VerifyMagicLinkScene />} />
              <Route path="/invite/:token" element={<AcceptInviteScene />} />
              <Route path="/slide-sim" element={<SlideSimulatorScene />} />
              {/* Public on purpose — freelancers and guest venues install
                  Slide Control without an AOTV login. Note the singular
                  /download/* path belongs to the Worker (the asset proxy);
                  this plural one is the human-facing page. */}
              <Route path="/downloads" element={<DownloadsScene />} />

              <Route
                path="/events"
                element={
                  <RequireAuth role="team">
                    <EventsScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/events/:id"
                element={
                  <RequireAuth role="team">
                    <EventScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/speakers"
                element={
                  <RequireAuth role="team">
                    <SpeakersScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/srt"
                element={
                  <RequireAuth role="team">
                    <SrtScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/streams"
                element={
                  <RequireAuth role="team">
                    <StreamsScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin-users"
                element={
                  <RequireAuth role="team">
                    <AdminUsersScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/slide-licenses"
                element={
                  <RequireAuth role="team">
                    <SlideLicensesScene />
                  </RequireAuth>
                }
              />

              <Route
                path="/me"
                element={
                  <RequireAuth role="speaker">
                    <SpeakerLandingScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/waiting/:eventId"
                element={
                  <RequireAuth role="speaker">
                    <WaitingRoomScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/stage/:eventId"
                element={
                  <RequireAuth role="speaker">
                    <StageScene />
                  </RequireAuth>
                }
              />
              <Route
                path="/rehearse/:eventId"
                element={
                  <RequireAuth role="speaker">
                    <RehearsalScene />
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </MeProvider>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
}
